import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import './Sidebar.css';
import {
  MdDashboard,
  MdAssignment,
  MdSmartToy,
  MdMenuBook,
  MdGroup,
  MdSecurity,
  MdReceiptLong,
  MdLayers,
  MdInsights,
  MdSettings,
  MdDescription,
  MdPerson,
  MdLock,
  MdArticle,
  MdClose,
  MdFolderCopy
} from 'react-icons/md';

import { IoNewspaperOutline } from "react-icons/io5";

const Sidebar = () => {
  const { user } = useContext(AuthContext);
  const { hasScope } = usePermissions();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  // Off-canvas drawer state for tablet / mobile widths (presentation only).
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // The navbar "Menu" button asks the sidebar to open: on small screens it
  // opens the drawer, on desktop it restores a hidden (collapsed) sidebar.
  useEffect(() => {
    const onToggle = () => {
      if (window.matchMedia('(max-width: 1024px)').matches) {
        setIsMobileOpen((open) => !open);
      } else {
        setIsCollapsed(false);
        document.body.classList.remove('sidebar-collapsed');
      }
    };
    window.addEventListener('app:toggle-sidebar', onToggle);
    return () => window.removeEventListener('app:toggle-sidebar', onToggle);
  }, []);

  // Close the drawer after navigating.
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle('sidebar-drawer-open', isMobileOpen);
    return () => document.body.classList.remove('sidebar-drawer-open');
  }, [isMobileOpen]);

  // Don't show sidebar on auth pages
  const authPages = ['/login', '/register'];
  if (!user || authPages.includes(location.pathname)) return null;

  const isActive = (path) => location.pathname === path;

  // An item is active on an exact path match OR when the current route is one of
  // its related sub-sections (e.g. the coordinator-permissions page lives under
  // User Management at /super-admin/coordinators/:id).
  const isItemActive = (item) =>
    location.pathname === item.path ||
    (Array.isArray(item.match) && item.match.some((m) => location.pathname.startsWith(m)));

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    // Add/remove class to body to adjust main content
    if (!isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  // Role-based menu items for a SPECIFIC role (so we can union across all of a
  // multi-role user's roles).
  const getMenuItems = (roleArg) => {
    const commonItems = [];

    switch (roleArg) {
      case 'superadmin':
        const superAdminItems = [
          { path: '/super-admin', icon: <MdDashboard />, label: 'Dashboard' },
          { path: '/super-admin/user-management', icon: <MdGroup />, label: 'User Management', match: ['/super-admin/coordinators'] },
          { path: '/super-admin/subjects', icon: <MdMenuBook />, label: 'Subjects' },
          { path: '/super-admin/activity-log', icon: <MdSecurity />, label: 'Activity Log' },
          { path: '/super-admin/token-ledger', icon: <MdReceiptLong />, label: 'Token Ledger' },
          { path: '/super-admin/question-insights', icon: <MdInsights />, label: 'Question Insights' },
          { path: '/super-admin/categorized-sets', icon: <MdLayers />, label: 'Manage Categorized Sets' },
          { path: '/super-admin/paper-management', icon: <IoNewspaperOutline />, label: 'Paper Management' },
          { path: '/super-admin/ai-provider', icon: <MdSettings />, label: 'AI Provider' },
          // { path: '/super-admin/reports', icon: <MdBarChart />, label: 'Reports' },
        ];

        // EduLab PDFs: reference books, managed by the superadmin and the CET admin.
        superAdminItems.push({ path: '/super-admin/edulab-pdfs', icon: <MdArticle />, label: 'EduLab PDFs' });

        return [...superAdminItems, ...commonItems];

      case 'cetadmin':
        return [
          { path: '/super-admin', icon: <MdDashboard />, label: 'Dashboard' },
          { path: '/super-admin/user-management', icon: <MdGroup />, label: 'User Management', match: ['/super-admin/coordinators'] },
          { path: '/super-admin/subjects', icon: <MdMenuBook />, label: 'Subjects' },
          { path: '/super-admin/activity-log', icon: <MdSecurity />, label: 'Activity Log' },
          { path: '/super-admin/token-ledger', icon: <MdReceiptLong />, label: 'Token Ledger' },
          { path: '/super-admin/question-insights', icon: <MdInsights />, label: 'Question Insights' },
          { path: '/super-admin/categorized-sets', icon: <MdLayers />, label: 'Manage Categorized Sets' },
          { path: '/super-admin/paper-management', icon: <IoNewspaperOutline />, label: 'Paper Management' },
          { path: '/super-admin/edulab-pdfs', icon: <MdArticle />, label: 'EduLab PDFs' },
          ...commonItems
        ];

      case 'cetcoordinator': {
        const items = [];
        if (hasScope('nav.dashboard')) {
          items.push({ path: '/coordinator', icon: <MdDashboard />, label: 'Dashboard' });
        }
        if (hasScope('nav.users')) {
          items.push({ path: '/coordinator/users', icon: <MdGroup />, label: 'User Management' });
        }
        if (hasScope('nav.subjects')) {
          items.push({ path: '/coordinator/subjects', icon: <MdMenuBook />, label: 'Subjects' });
        }
        if (hasScope('nav.activity')) {
          items.push({ path: '/coordinator/activity-log', icon: <MdSecurity />, label: 'Activity Log' });
        }
        if (hasScope('nav.insights')) {
          items.push({ path: '/coordinator/question-insights', icon: <MdInsights />, label: 'Question Insights' });
        }
        if (hasScope('nav.tokenledger')) {
          items.push({ path: '/coordinator/token-ledger', icon: <MdReceiptLong />, label: 'Token Ledger' });
        }
        if (hasScope('nav.categorizedsets')) {
          items.push({ path: '/coordinator/categorized-sets', icon: <MdLayers />, label: 'Manage Categorized Sets' });
        }
        if (hasScope('papers.manage')) {
          items.push({ path: '/coordinator/paper-management', icon: <IoNewspaperOutline />, label: 'Paper Management' });
        }
        if (hasScope('papers.lock')) {
          items.push({ path: '/coordinator/papers', icon: <MdDescription />, label: 'Paper Publishing' });
        }
        return [...items, ...commonItems];
      }

      case 'moderator':
        return [
          { path: '/moderator', icon: <MdDashboard />, label: 'Dashboard' },
          { path: '/moderator-categorization', icon: <MdAssignment />, label: 'Categorize Papers' },
          { path: '/moderator/stored-papers', icon: <MdFolderCopy />, label: 'Stored Papers' },
          { path: '/my-subjects', icon: <MdMenuBook />, label: 'My Subjects' },
          // Faculty / user management is handled by super admin / CET admin / coordinator — not moderators.
          // { path: '/moderator/approvals', icon: <MdCheckCircle />, label: 'Approvals' },
          ...commonItems,
        ];

      case 'subject_matter_expert':
        return [
          { path: '/subject-matter-expert', icon: <MdDashboard />, label: 'Dashboard' },
          { path: '/sme-papers', icon: <MdAssignment />, label: 'Review Papers' },
          { path: '/my-subjects', icon: <MdMenuBook />, label: 'My Subjects' },
          // Examiner management is handled by super admin / CET admin / coordinator — not SMEs.
          ...commonItems,
        ];

      case 'examiner':
        return [
          { path: '/examiner', icon: <MdDashboard />, label: 'Dashboard' },
          { path: '/examiner/create', icon: <MdSmartToy />, label: 'Generate Questions' },
          { path: '/examiner/papers', icon: <MdDescription />, label: 'My Papers' },
          { path: '/paper-templates', icon: <MdAssignment />, label: 'Paper Templates' },
          { path: '/my-subjects', icon: <MdMenuBook />, label: 'My Subjects' },
          ...commonItems,
        ];

      default:
        return commonItems;
    }
  };

  // The sidebar reflects ONLY the active (currently-switched) role — selecting a
  // role from the top-bar switcher changes user.role, which shows that role's
  // tabs and that role's dashboard. Multi-role users switch to see another role.
  const menuItems = getMenuItems(user.role);

  const initials = (user.name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  return (
    <>
      {isMobileOpen && (
        <div className="sidebar-backdrop" onClick={() => setIsMobileOpen(false)} aria-hidden="true" />
      )}
      <aside
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
        aria-label="Primary navigation"
      >
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <img src="/favicon.png" alt="Logo" className="brand-logo" />
            <div className="brand-copy">
              <span className="brand-text">CET QPG</span>
              <span className="brand-sub">Question Paper Generation</span>
            </div>
          </div>
          <button
            type="button"
            className="sidebar-toggle sidebar-toggle-desktop"
            onClick={toggleSidebar}
            title="Hide navigation"
          >
            Hide
          </button>
          <button
            type="button"
            className="sidebar-toggle sidebar-toggle-mobile"
            onClick={() => setIsMobileOpen(false)}
          >
            <MdClose className="ui-icon" aria-hidden="true" /> Close
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Navigation</div>
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${isItemActive(item) ? 'active' : ''}`}
            >
              <span className="sidebar-icon" aria-hidden="true">{item.icon}</span>
              <span className="sidebar-label">{item.label}</span>
            </Link>
          ))}

          <div className="sidebar-section-label">Account</div>
          {/* Profile Dropdown */}
          <div className="sidebar-dropdown">
            <Link
              to="/profile"
              className={`sidebar-link dropdown-toggle ${(isActive('/profile') || isActive('/2fa-security')) ? 'active' : ''
                }`}
              onClick={(e) => {
                // Navigate to profile page
                // Also toggle dropdown on click
                setTimeout(() => toggleProfileDropdown(), 0);
              }}
            >
              <span className="sidebar-icon" aria-hidden="true"><MdPerson /></span>
              <span className="sidebar-label">Profile</span>
              <span
                className={`dropdown-arrow ${isProfileDropdownOpen ? 'open' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleProfileDropdown();
                }}
                aria-hidden="true"
              />
            </Link>

            {isProfileDropdownOpen && !isCollapsed && (
              <div className="dropdown-menu">
                <Link
                  to="/2fa-security"
                  className={`dropdown-item ${location.pathname === '/2fa-security' ? 'active' : ''}`}
                >
                  <span className="sidebar-icon" aria-hidden="true"><MdLock /></span>
                  <span>2FA Security</span>
                </Link>
              </div>
            )}
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              {user.avatar_url ? (
                <img
                  src={`${process.env.REACT_APP_USE_PRODUCTION === 'true' ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_DEV_API_URL}${user.avatar_url}`}
                  alt=""
                />
              ) : (
                initials
              )}
            </div>
            <div className="user-info">
              <div className="user-name">{user.name}</div>
              <div className="user-role">{user.role.replace('_', ' ')}</div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
